import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

const ForgotPassword = () => {
 useDocumentMetadata(
  'Forgot Password — Dakhla',
  'Reset your Dakhla account password to regain access to your applications and profile.'
 );

 const [formData, setFormData] = useState({
 role: 'student',
 email: ''
 });
 const [loading, setLoading] = useState(false);
 const [message, setMessage] = useState('');
 const [error, setError] = useState('');
 const [emailSent, setEmailSent] = useState(false);

 const handleChange = (e) => {
 setFormData({
 ...formData,
 [e.target.name]: e.target.value
 });
 if (error) setError('');
 if (message) setMessage('');
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 setLoading(true);
 setError('');
 setMessage('');

 try {
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const endpoint = formData.role === 'student'
 ? `${apiUrl}/student/forgot-password`
 : `${apiUrl}/university/forgot-password`;

 const response = await fetch(endpoint, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({
 email: formData.email
 }),
 });

 const data = await response.json();

 if (response.ok) {
 setMessage(data.message || 'Password reset link has been sent to your email.');
 setEmailSent(true);
 } else {
 setError(data.message || 'Failed to send reset email. Please try again.');
 }
 } catch (error) {
 console.error('Forgot password error:', error);
 setError('Network error. Please check your connection and try again.');
 } finally {
 setLoading(false);
 }
 };

 return (
 <div className="min-h-screen bg-slate-50 relative flex flex-col justify-center py-12 sm:px-6 lg:px-8 overflow-hidden">
 {/* Background decoration */}
 <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
 <div className="absolute top-0 left-0 w-full h-full opacity-[0.025] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:24px_24px]" />
 <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-primary-500/8 blur-3xl animate-pulse-ring" style={{ animationDuration: '9s' }} />
 <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-indigo-500/8 blur-3xl animate-pulse-ring" style={{ animationDuration: '12s' }} />
 </div>

 <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10 px-4 sm:px-0">
 <div className="text-center">
 <h2 className="text-3xl font-extrabold text-slate-800 tracking-tight">
 Reset your password
 </h2>
 <p className="mt-2 text-sm text-slate-500">
 Enter your email address and we'll send you a link to reset your password.
 </p>
 </div>
 </div>

 <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10 px-4 sm:px-0">
 <div className="card relative overflow-hidden bg-white py-8 px-5 sm:rounded-2xl sm:px-10 border border-slate-200 shadow-lg shadow-slate-100">
 {/* Accent line */}

 {!emailSent ? (
 <form className="space-y-6" onSubmit={handleSubmit}>
 {error && (
 <div className="p-3.5 bg-red-50 border border-red-200 text-red-600 rounded-xl text-xs font-semibold flex items-center gap-2">
 <span className="text-sm leading-none">⚠️</span>
 <span>{error}</span>
 </div>
 )}

 <div>
 <label className="block text-xs font-semibold text-slate-700 mb-2">
 I am a:
 </label>
 <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
 <button
 type="button"
 onClick={() => setFormData({ ...formData, role: 'student' })}
 className={`flex-1 py-2 px-3 text-xs font-semibold rounded-lg transition-all duration-200 ${
 formData.role === 'student'
 ? 'bg-white text-primary-700 shadow-sm border border-slate-200'
 : 'text-slate-500 hover:text-slate-700'
 }`}
 >
 Student
 </button>
 <button
 type="button"
 onClick={() => setFormData({ ...formData, role: 'university' })}
 className={`flex-1 py-2 px-3 text-xs font-semibold rounded-lg transition-all duration-200 ${
 formData.role === 'university'
 ? 'bg-white text-primary-700 shadow-sm border border-slate-200'
 : 'text-slate-500 hover:text-slate-700'
 }`}
 >
 University
 </button>
 </div>
 </div>

 <div>
 <label htmlFor="email" className="block text-xs font-semibold text-slate-700 mb-1">
 Email address
 </label>
 <input
 id="email"
 name="email"
 type="email"
 autoComplete="email"
 required
 value={formData.email}
 onChange={handleChange}
 className="appearance-none block w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm"
 placeholder="Enter your email address"
 />
 </div>

 <div>
 <button
 type="submit"
 disabled={loading}
 className={`w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-xl text-sm font-semibold text-white shadow-md active:scale-[0.98] transition-premium cursor-pointer ${
 loading
 ? 'bg-slate-400 cursor-not-allowed shadow-none'
 : 'bg-gradient-to-r from-primary-600 to-indigo-600 hover:from-primary-700 hover:to-indigo-700 shadow-primary-500/20 hover:shadow-lg hover:shadow-primary-500/25 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
 }`}
 >
 {loading ? (
 <>
 <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
 </svg>
 <span>Sending...</span>
 </>
 ) : (
 'Send Reset Link'
 )}
 </button>
 </div>
 </form>
 ) : (
 <div className="text-center py-4">
 <div className="p-3.5 bg-green-50 border border-green-200 text-green-600 rounded-xl text-xs font-semibold flex items-center gap-2 mb-6 justify-center">
 <span className="text-sm leading-none">✅</span>
 <span>{message}</span>
 </div>
 <p className="text-sm text-slate-655 mb-4 leading-relaxed font-medium">
 Please check your email and click the link to reset your password.
 </p>
 <p className="text-xs text-slate-400">
 Didn't receive the email? Check your spam folder or request a new link.
 </p>
 </div>
 )}

 <div className="mt-6 pt-4 border-t border-slate-100 flex justify-center">
 <Link
 to="/login"
 className="flex items-center justify-center text-sm font-semibold text-primary-600 hover:text-primary-500 transition-colors"
 >
 <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
 Back to login
 </Link>
 </div>
 </div>
 </div>
 </div>
 );
};

export default ForgotPassword;
